import _ from 'lodash';
import PropTypes from 'prop-types';

import utils from 'utils/utils';
import useWindowDimensions from 'hooks/useWindowDimensions';

import helpers, { getEffectiveCheckboxOption, getEffectiveSelectable } from 'helpers/proposal';
import { RTL_LANGUAGES } from 'constants/languages';
import { isIphone } from 'utils/device';
import { openImagePreview } from '../../lib/imagePreviewRenderer';

import './Tex.scss';

const TeXHTMLString = ({ content, configText, props, blockIndex }) => {
  const { width, isMobile } = useWindowDimensions();
  const isIphoneDevice = isIphone();

  // Ensure openImagePreview is available globally
  if (!window.openImagePreview) {
    window.openImagePreview = openImagePreview;
  }

  let json;

  try {
    json = JSON.parse(content);
  } catch (exx) {
    return '[pricing block]';
  }

  if (!json) {
    return '[pricing block]';
  }

  const decimalPrecision = props?.prop?.decimalPrecision || 2;
  const isDotSeparator = props?.prop?.priceSeperator === '.';
  const prop = props?.prop;
  const isRtl =
    prop &&
    (RTL_LANGUAGES.includes(prop?.language?.toLowerCase()) ||
      RTL_LANGUAGES.includes(prop?.draft?.section?.language?.toLowerCase()) ||
      RTL_LANGUAGES.includes(prop?.draft?.item?.language?.toLowerCase()));

  if (json.superPowers) {
    const jsonobj = json.superPowers;
    let bigbody = [];

    const supers = configText('superpowers simple');
    const superToText = {};
    _.each(supers.split('\n'), (l) => {
      // superToText[l.split(':')[0]] = l.split(':')[1].trim();
    });

    const fakelist = _.map(jsonobj, (v, k) => ({
      name: k,
      super: {
        text: v && typeof v.text === 'string' ? v.text : superToText[k],
        order: v.order || 0,
        description: v.description || '',
      },
    })).sort((a, b) => a.super.order - b.super.order);

    let body = _.compact(
      _.map(fakelist, (x, superIndex) => {
        const supert = x.super;

        if (supert.text === '') {
          return;
        }

        const description = supert.description && (
          <p className="item-description" style={{ wordBreak: 'break-word' }}>
            {supert.description}
          </p>
        );

        if (supert.description)
          return (
            <li className="super-li" key={superIndex}>
              <div>{supert.text || ''}</div>
              {description}
            </li>
          );
        return (
          <li className="super-li" key={superIndex}>
            {supert.text || ''}
          </li>
        );
      })
    );

    bigbody.push(
      <div className="texblockbody">
        <ol>{body}</ol>
      </div>
    );

    return bigbody;
  }
  // -------- END of superPowers --------

  if (json.milestones) {
    const jsonobj = json.milestones;

    // if list doesnt exist then, return empty arr
    if (!jsonobj?.chosen || (jsonobj.chosen && Object.keys(jsonobj.chosen).length === 0)) {
      return [];
    }

    let bigbody = [];

    const [fkey] = Object.keys(jsonobj.chosen);
    jsonobj.chosen = { [fkey]: jsonobj.chosen[fkey] };

    _.map(jsonobj.chosen, (v, chosenKey) => {
      // max length of time colum text
      const timeLength = v.list.reduce((acc, x) => Math.max(acc, (x?.time || '').trim().length), 0);
      // if name or description length > 120 limit time column to 'medium'
      const isLargeName =
        timeLength > 0 && v.list.some((x) => x?.name?.length > 60 || x?.description?.length > 80);
      // dynamic size of time column based on content
      const timeColumnSize = getColumnSize(timeLength, isLargeName);

      let body = _.map(v.list, (x, milestonesIndex) => {
        const { time } = x || {};
        const trclass = 'timerow tablerow';
        const timetd = timeLength > 0 && (
          <td className={`time ${timeColumnSize}`}>
            <div className="time-text">{time || ''}</div>
          </td>
        );

        const description = x?.description && (
          <p className="item-description" style={{ wordBreak: 'break-word' }}>
            {x.description}
          </p>
        );

        if (x?.description)
          return (
            <tr className={trclass} key={milestonesIndex}>
              <td className="name">
                <div>{x?.name}</div>
                {description}
              </td>
              {timetd}
            </tr>
          );
        return (
          <tr className={trclass} key={milestonesIndex}>
            <td className="name">{x?.name}</td>
            {timetd}
          </tr>
        );
      });

      bigbody.push(
        <div key={chosenKey} className="texblockbody">
          <table>
            <tbody className="texblockbodyinner">{body}</tbody>
          </table>
        </div>
      );
    });

    return bigbody;
  }
  // -------- END of milestones --------

  if (json.deliverables) {
    const jsonobj = json.deliverables;
    let bigbody = [];

    const curr = json.curr || props.prop.currency || (configText ? configText('currency') : '$');
    const [fkey] = Object.keys(jsonobj.chosen);
    let roundTotal = json.roundTotal ? json.roundTotal : false;

    json.pricing = json.pricing || { strategy: 'table' };
    const { strategy } = json.pricing;

    jsonobj.chosen = strategy === 'value' ? jsonobj.chosen : { [fkey]: jsonobj.chosen[fkey] };
    let jsonobjFkey = jsonobj?.chosen[fkey];

    // switch if tax is type -1 or the other way
    helpers.taxdisfix(jsonobj.chosen);

    let pkgnum = 0;
    const newchosen = {};

    let selectable = undefined;

    if (jsonobjFkey) {
      ({ selectable } = jsonobjFkey);
    }

    if (strategy !== 'value' && jsonobjFkey?.list) {
      const _temp = window.structuredClone
        ? window.structuredClone(jsonobjFkey)
        : JSON.parse(JSON.stringify(jsonobjFkey));

      const items = _temp.list;
      roundTotal = _temp.roundTotal; //eslint-disable-line

      items.forEach((i, itemIndex) => {
        if (!!i.type) {
          newchosen[i.type] = newchosen[i.type] ? newchosen[i.type] : {};
          newchosen[i.type].name = i.type;
          newchosen[i.type].quantity = _temp.quantity;
          newchosen[i.type].tax = _temp.tax;
          newchosen[i.type].discount = _temp.discount;
          newchosen[i.type].subtotal = _temp.subtotal;
          newchosen[i.type].showTotal = _temp.showTotal;
          newchosen[i.type].totalText = _temp.totalText;
          newchosen[i.type].subtotalText = _temp.subtotalText;
          newchosen[i.type].subtotalBeforeTax = _temp?.subtotalBeforeTax || '';
          newchosen[i.type].subtotalBeforeTaxText = _temp?.subtotalBeforeTaxText || '';
          newchosen[i.type]['subtotalBeforeTaxTextOne-Time'] =
            _temp?.['subtotalBeforeTaxTextOne-Time'] || '';
          newchosen[i.type].subtotalBeforeTaxTextHourly = _temp?.subtotalBeforeTaxTextHourly || '';
          newchosen[i.type].subtotalBeforeTaxTextMonthly =
            _temp?.subtotalBeforeTaxTextMonthly || '';
          newchosen[i.type].subtotalBeforeTaxTextAnnually =
            _temp?.subtotalBeforeTaxTextAnnually || '';
          newchosen[i.type].selectable = _temp.selectable;
          newchosen[i.type].pricing = json.pricing;
          newchosen[i.type].structured = _temp?.structured || false;
          newchosen[i.type].columnName = _temp?.columnName || {};
          newchosen[i.type].list = newchosen[i.type]?.list?.length ? newchosen[i.type].list : [];
          newchosen[i.type].list.push(i);
          newchosen[i.type].customColumns = _temp?.customColumns?.map((column, columnIndex) => ({
            name: column.name,
            values: [
              ...(newchosen[i.type]?.customColumns?.[columnIndex]?.values || []),
              column.values[itemIndex],
            ],
          }));
          newchosen[i.type].thumbnail = _temp?.thumbnail || false;
        } else {
          newchosen[0] = newchosen[0] ? newchosen[0] : {};
          newchosen[0].name = 0;
          newchosen[0].quantity = _temp.quantity;
          newchosen[0].tax = _temp.tax;
          newchosen[0].discount = _temp.discount;
          newchosen[0].subtotal = _temp.subtotal;
          newchosen[0].showTotal = _temp.showTotal;
          newchosen[0].totalText = _temp.totalText;
          newchosen[0].subtotalText = _temp.subtotalText;
          newchosen[0].subtotalBeforeTax = _temp?.subtotalBeforeTax || '';
          newchosen[0].subtotalBeforeTaxText = _temp?.subtotalBeforeTaxText || '';
          newchosen[0]['subtotalBeforeTaxTextOne-Time'] =
            _temp?.['subtotalBeforeTaxTextOne-Time'] || '';
          newchosen[0].subtotalBeforeTaxTextHourly = _temp?.subtotalBeforeTaxTextHourly || '';
          newchosen[0].subtotalBeforeTaxTextMonthly = _temp?.subtotalBeforeTaxTextMonthly || '';
          newchosen[0].subtotalBeforeTaxTextAnnually = _temp?.subtotalBeforeTaxTextAnnually || '';
          newchosen[0].selectable = _temp.selectable;
          newchosen[0].pricing = json.pricing;
          newchosen[0].structured = _temp?.structured || false;
          newchosen[0].columnName = _temp?.columnName || {};
          newchosen[0].list = newchosen[0]?.list?.length ? newchosen[0].list : [];
          newchosen[0].list.push(i);
          newchosen[0].customColumns = _temp.customColumns;
          newchosen[0].thumbnail = _temp?.thumbnail || false;
        }
      });
      jsonobj.chosen = newchosen;

      // eslint-disable-next-line prefer-destructuring
      jsonobjFkey = newchosen[0];
    }

    const isSubtotalValueUndefined = jsonobjFkey?.subtotalValue === undefined;

    const calcDiscPrice = (price, discount, symbol, isDotSeparator) => {
      if (isDotSeparator) {
        discount = Number(String(discount).replaceAll(',', '.'));
        price = Number(String(price).replaceAll(',', '.'));
      }
      if (discount && price) {
        if (symbol === '%') {
          if (discount >= 100) {
            if (discount > 100) return price;
          }
          price = price - price * (discount / 100);
        } else {
          price = price - discount;
        }
      }
      return price;
    };

    const calculateSubTotal = (prop, chosen) => {
      if (strategy) {
        // calculate sub total of all items in the list/manual input

        if (strategy === 'table') {
          // add all price, or add all price * items
          if (chosen?.list) {
            let totes = 0;
            chosen.list.forEach((item) => {
              const discVal = item?.discount?.value || 0;
              const discUnit = item?.discount?.unit || '%';

              const price =
                prop.priceSeperator === '.'
                  ? item?.currentPrice
                    ? String(item?.currentPrice)?.replaceAll(',', '.')
                    : String(item?.price)?.replaceAll(',', '.')
                  : item?.currentPrice
                  ? item?.currentPrice
                  : item?.price;

              const disc =
                prop.priceSeperator === '.' ? String(discVal)?.replaceAll(',', '.') : discVal;

              const checkboxOpt = getEffectiveCheckboxOption(item, chosen.selectable);
              const itemCounts =
                !checkboxOpt.on ||
                (typeof item.checked !== 'undefined' ? item.checked : checkboxOpt.markedByDefault);

              let subTotal = 0;

              if (itemCounts) {
                subTotal = calcDiscPrice(
                  parseFloat(price * (chosen?.quantity ? item.items || 1 : 1)),
                  parseFloat(disc * (discUnit !== '%' && chosen?.quantity ? item.items || 1 : 1)),
                  discUnit,
                  prop.priceSeperator === '.'
                );
              }
              totes += Number(subTotal) || 0;
            });
            return totes;
          }
          return 0;
        } else {
          // getting the subtotal value from input
          return chosen?.subtotalValue || 0;
        }
      }
      return chosen?.subtotalValue || 0;
    };

    // get header text
    const getHeaderText = (value, defaultValue) => {
      return typeof value === 'string' ? value : defaultValue;
    };

    const dotSeparator = (value) => {
      return isDotSeparator ? value.toString().replace('.', ',') : value;
    };

    const concatCurrency = (value = 0, options) => {
      // replace . with , for '.' separator
      value = dotSeparator(value);

      return helpers.concatcurr({
        prop: props?.prop || null,
        x: value,
        curr: curr,
        limitDec: roundTotal ? 0 : decimalPrecision,
        // minimum: 0,
        roundedTotal: roundTotal,
        parsingDecimal: roundTotal ? false : true,
        ...options,
      });
    };

    _.map(jsonobj.chosen, (v, deliverablesIndex) => {
      pkgnum++;
      let calctotal = calculateSubTotal(props?.prop, v);
      const subTotal = JSON.parse(JSON.stringify(calctotal));

      let pkgname = v?.name || deliverablesIndex.toUpperCase();
      const pkgnameTranslated = configText ? configText(pkgname) || pkgname : pkgname;

      // whether table or value strategy
      const tableStrategy = strategy !== 'value';
      const chosenKey = tableStrategy ? pkgname : fkey;
      const jsonobjChosen = jsonobj?.chosen[chosenKey];

      // if structured is checked
      const structured = !!v?.structured;

      // should show qty column ?
      // const qtyColumn = (!tableStrategy && v?.quantity && (v.type === 'Monthly' || v.type === 'Annually')) || (json.pricing.strategy !== 'value' && v?.quantity) ? true : false;
      const qtyColumn =
        (!tableStrategy && v?.quantity) || (strategy !== 'value' && v?.quantity) ? true : false;

      // custom column
      const customColumns = v?.customColumns || [];

      const getBody = props?.prop?.draft?.bodyFont?.fontSize || false;

      roundTotal = !tableStrategy ? v.roundTotal : roundTotal;
      selectable = v?.selectable;

      // find the largest number, i.e summedPrice
      let largestPriceIndex = null;
      const tableId = utils.getRandomString();
      if (isIphoneDevice) {
        /*
        for iphone calculate the width of the largest price column and set the min width
        else the columns shrink and text overlaps with the next column
        */
        v?.list?.reduce((max, item, deliverablesBodyIndex) => {
          const currentPrice = item?.currentPrice || item?.price || 0;
          const currentPriceLength = currentPrice.length;
          if (currentPriceLength > max) {
            largestPriceIndex = deliverablesBodyIndex;
          }
          return Math.max(max, currentPriceLength);
        }, 0);

        const tableTdId = `price-${tableId}-${largestPriceIndex}`;

        setTimeout(() => {
          const tableTd = document.getElementById(tableTdId);
          if (tableTd) {
            const minWidth = tableTd.offsetWidth + 10;

            // add style to div with class name price-${tableId}
            const priceDivs = document.getElementsByClassName(`price-${tableId}`);
            if (priceDivs) {
              priceDivs.forEach((priceDiv) => {
                priceDiv.style.minWidth = minWidth + 'px';
              });
            }
          }
        }, 50);
      }

      let body = _.map(v?.list, (x, deliverablesBodyIndex) => {
        const discountUnit = x?.discount?.unit;
        const discountValue = x?.discount?.value;

        const calcDiscountPrice = () => {
          if (discountUnit === '%') {
            // If the discount is in percentage, we'll calculate the discounted price.
            if (discountValue >= 0 && discountValue <= 100) {
              const discountAmount = (discountValue / 100) * x?.currentPrice;
              return x?.currentPrice - discountAmount || x.price || 0;
            }
          } else {
            // If the discount unit is not %, we'll directly subtract it from the item price.
            if (discountValue > 0) {
              return discountValue ? x?.currentPrice - discountValue : x?.price || 0;
            } else {
              return x.currentPrice || x.price || 0;
            }
          }
        };

        const discPrice = calcDiscountPrice();
        // const currentPrice = helpers.concatcurr({
        //   prop: props?.prop || null,
        //   x: x.currentPrice || x.price || 0,
        //   curr,
        //   limitDec: roundTotal ? 0 : decimalPrecision,
        //   roundedTotal: roundTotal,
        //   parsingDecimal: roundTotal ? false : true,
        // });
        const currentPrice = concatCurrency(x.currentPrice || x.price || 0);

        // const discWithCurr = helpers.concatcurr({
        //   prop: props?.prop || null,
        //   x: x?.discount?.value,
        //   curr,
        //   limitDec: roundTotal ? 0 : decimalPrecision,
        //   roundedTotal: roundTotal,
        //   parsingDecimal: roundTotal ? false : true,
        // });
        const discWithCurr = concatCurrency(x?.discount?.value);

        // const summedCurrentPrice = helpers.concatcurr({
        //   prop: props?.prop || null,
        //   x: x.currentPrice || 0,
        //   y: v?.quantity ? x.items : 1,
        //   curr,
        //   limitDec: roundTotal ? 0 : decimalPrecision,
        //   roundedTotal: roundTotal,
        //   parsingDecimal: roundTotal ? false : true,
        // });
        const summedCurrentPrice = concatCurrency(x.currentPrice || 0, {
          y: v?.quantity ? x.items : 1,
        });

        // const summedPrice = helpers.concatcurr({
        //   prop: props?.prop || null,
        //   x: discPrice || 0,
        //   y: v?.quantity ? x.items : 1,
        //   curr,
        //   limitDec: roundTotal ? 0 : decimalPrecision,
        //   roundedTotal: roundTotal,
        //   parsingDecimal: roundTotal ? false : true,
        // });
        const summedPrice = concatCurrency(discPrice || 0, { y: v?.quantity ? x.items : 1 });

        x.items = x.items || 1;

        const trclass =
          deliverablesBodyIndex === v.list.length - 1
            ? 'tablerow finalrow'
            : !tableStrategy
            ? 'pricerow valuerow tr-340'
            : 'pricerow tablerow tr-341';

        const pricetdStyle = selectable && tableStrategy ? { flex: 0.3 } : {};

        let disctdStyle = { display: 'none' };
        if (discountValue && discountValue !== '') {
          disctdStyle = {};
        }

        /*
        const discVal = helpers.concatcurr({
          prop: props?.prop || null,
          x:
            parseFloat(
              props?.prop.priceSeperator === '.'
                ? String(x?.discount?.value).replaceAll(',', '.')
                : String(x?.discount?.value)
            ) * (x?.discount?.unit !== '%' && v?.quantity ? x.items || 1 : 1),
          curr: x?.discount?.unit || curr,
          limitDec: decimalPrecision,
          roundedTotal: true,
        });
        */

        const discText = configText ? configText('discount text') : 'Discount';
        let pricetd = '';

        if (strategy === 'value') {
          pricetd = (
            <>
              {structured && qtyColumn && <td className="quantity td-373">{x?.items || ''}</td>}
              <td className="price td-376" />
            </>
          );
        } else {
          pricetd = (
            <>
              {customColumns &&
                customColumns?.map((customColumn, index) => (
                  <td key={index} className="custom-column" style={pricetdStyle}>
                    <div>{customColumn?.values[deliverablesBodyIndex]}</div>
                  </td>
                ))}
              {structured && qtyColumn && (
                <td className="quantity td-380" style={pricetdStyle}>
                  {x?.items || ''}
                </td>
              )}
              <td className={`price td-383 ${`price-${tableId}`}`} style={pricetdStyle}>
                {isMobile ? (
                  <>
                    <div
                      className="price-amount no-break no-rtl"
                      id={`price-${tableId}-${deliverablesBodyIndex}`}>
                      {summedPrice}
                    </div>
                    <div className="price-discount-style" style={disctdStyle}>
                      <Amount amount={<del>{summedCurrentPrice}</del>} />
                    </div>
                  </>
                ) : (
                  <div className={`price-amount ${summedCurrentPrice.length > 10 ? 'long' : ''}`}>
                    <span style={disctdStyle}>
                      <del className="no-break no-rtl">{summedCurrentPrice}</del>
                      &nbsp;
                    </span>
                    <span className="no-break no-rtl">{summedPrice}</span>
                  </div>
                )}
                {qtyColumn && x.items > 1 && (
                  <div className={`price-discount-style`}>
                    {structured ? (
                      <>
                        <span>
                          {getHeaderText(
                            jsonobjChosen.columnName?.rowprice,
                            configText('rowprice')
                          )}
                        </span>
                        &nbsp;
                        <span className={`discount-amount`}>
                          <Amount amount={currentPrice} />
                        </span>
                      </>
                    ) : (
                      <Amount amount={`${x?.items} × ${currentPrice}`} />
                    )}
                  </div>
                )}
                <div style={disctdStyle} className={`price-discount-style`}>
                  <span>{discText}:</span>
                  &nbsp;
                  <Amount amount={discountUnit === '%' ? `${discountValue || 0}%` : discWithCurr} />
                </div>
              </td>
            </>
          );
        }

        // const forHourly =
        //   v.quantity &&
        //   x.quantity &&
        //   (v.name === 'Hourly' || v.type === 'Hourly') &&
        //   `(for ${x.quantity} ${x.quantity > 1 ? 'hours' : 'hours'})`;
        // const forMonthly =
        //   v.quantity &&
        //   x.quantity &&
        //   (v.name === 'Monthly' || v.type === 'Monthly') &&
        //   `(for ${x.quantity} ${x.quantity > 1 ? 'months' : 'month'})`;
        // const forAnnually =
        //   v.quantity &&
        //   x.years &&
        //   (v.name === 'Annually' || v.type === 'Annually') &&
        //   `(for ${x.years} ${x.years > 1 ? 'years' : 'year'})`;

        const isMatch = (label) => v.quantity && (v.name === label || v.type === label);
        const getLabel = (label, qty) => `(for ${qty} ${label}${qty > 1 ? 's' : ''})`;

        let durationLabel = x.quantity && isMatch('Hourly') && getLabel('hour', x.quantity);
        if (!durationLabel) {
          durationLabel = x.quantity && isMatch('Monthly') && getLabel('month', x.quantity);
        }
        if (!durationLabel) {
          durationLabel = x.years && isMatch('Annually') && getLabel('year', x.years);
        }

        const description = x.description && <>{x.description}</>;

        const itemSelectable = getEffectiveSelectable(x, selectable);
        const itemCheckboxOpt = getEffectiveCheckboxOption(x, selectable);
        const tdCheckboxDisabled = props.prop.state === 'approved' || itemSelectable === 'locked';
        let tdCheckboxStyle = {};
        let isSmallScreen = width <= 480;

        if (isSmallScreen) {
          tdCheckboxStyle = { flex: '0.1', height: 20 };
        } else {
          const bodyFontSize = parseInt(getBody);
          tdCheckboxStyle = {
            ...(isRtl ? { right: '0px' } : { left: width < 768 ? '10px' : '14px' }),
            height: bodyFontSize + bodyFontSize * 0.4,
            position: 'absolute',
          };
        }

        const descStyles = isRtl ? { right: '20px' } : { left: '28px' };

        let itemDivStyle = {
          position: isSmallScreen ? 'inherit' : 'relative',
          bottom: '0',
          ...descStyles,
        };

        const defaultChecked =
          typeof x.checked !== 'undefined' ? x.checked : itemCheckboxOpt.markedByDefault;

        const tdCheckbox = itemSelectable && (
          <div
            className="pricing-layout-check-box"
            key={`${deliverablesBodyIndex}-td-checkbox-${defaultChecked}`}
            style={{ ...tdCheckboxStyle }}>
            <label className="nobreak-secondary-checkbox has-top-padding" id={props.name}>
              <input
                type="checkbox"
                defaultChecked={defaultChecked}
                // for table always use index 0, for value use 0,1,2...
                value={tableStrategy ? 0 : pkgnum - 1}
                disabled={tdCheckboxDisabled ? true : false}
                id={`${x.id}-${blockIndex}-${deliverablesBodyIndex}-${props.name}`}
                className="priceTableCheckbox"
              />
              <span className="nobreak-secondary-checkbox-checkmark h2" />
            </label>
          </div>
        );

        if (
          window.location.href.indexOf('/cd/') !== -1 ||
          window.location.href.indexOf('/pd/') !== -1
        ) {
          window[`${x.id}`] = { images: x.images };
        }

        return (
          <tr
            className={trclass}
            style={tdCheckbox ? { position: 'relative' } : {}}
            key={`${deliverablesBodyIndex}-tr`}>
            <td
              key={`${deliverablesBodyIndex}-td`}
              className="name td-457"
              style={tdCheckbox ? { flex: '0.7' } : {}}>
              {tdCheckbox}
              {x.images?.length > 0 && v.thumbnail && (
                <>
                  <div
                    className="image-preview-container"
                    style={tdCheckbox ? itemDivStyle : {}}
                    id={`price-image-preview-${x.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (window.openImagePreview && x.images?.length > 0) {
                        window.openImagePreview(x.images, 0, blockIndex);
                      }
                    }}>
                    {x.images?.length > 1 && (
                      <>
                        <div className="inner-border-1"></div>
                        <div className="inner-border-2"></div>
                      </>
                    )}
                    <img
                      className="price-image-preview"
                      alt={x.images?.[0]?.name || x.name || 'product'}
                      src={x.images?.[0]?.url}
                      id={x.id}
                    />
                  </div>
                </>
              )}

              <div className="item-title" style={tdCheckbox ? itemDivStyle : {}}>
                {x.name} {durationLabel}
              </div>
              <p
                className="item-description"
                style={tdCheckbox && !isSmallScreen ? descStyles : {}}>
                {x.description && description}
              </p>
            </td>
            {pricetd}
          </tr>
        );
      });

      if (Object.keys(jsonobj.chosen).length === 1 && v?.name === 'One-Time') pkgname = '0';

      let paymentType = v?.type;
      paymentType = configText && paymentType ? `(${configText(paymentType) || paymentType})` : '';

      const opheadline = !tableStrategy ? (
        <div className={`pkg-name pkg-${pkgnum}`} style={{ fontSize: `1.25em` }}>
          <b>
            <u>
              {pkgnameTranslated}
              {paymentType}
            </u>
          </b>
        </div>
      ) : (
        pkgname !== '0' && (
          <div className={`pkg-name pkg-${pkgnum}`} style={{ fontSize: `1.25em` }}>
            <b>
              <u>{pkgnameTranslated}</u>
            </b>
          </div>
        )
      );

      // table heading
      let tableHeading = null;

      tableHeading = structured && (
        <tr className="pricerow tablerow tr-507">
          <td className="name">
            <div>
              {getHeaderText(jsonobjChosen.columnName?.deliverable, configText('Deliverable'))}
            </div>
          </td>

          {jsonobjChosen.customColumns?.map((column, index) => (
            <td key={index} className="custom-column">
              <div>{column.name}</div>
            </td>
          ))}

          {((tableStrategy && jsonobjChosen.quantity) || (!tableStrategy && qtyColumn)) && (
            <td className="quantity td-513">
              <div>
                {isMobile
                  ? getHeaderText(jsonobjChosen.columnName?.item, configText('QTY'))
                  : getHeaderText(jsonobjChosen.columnName?.item, configText('quantity'))}
              </div>
            </td>
          )}
          <td className="price td-519">
            <div>
              {getHeaderText(jsonobjChosen.columnName?.price, configText('line total text'))}
            </div>
          </td>
        </tr>
      );

      let subtotalhtml = '';
      let totalhtml = '';
      const subt = !!v?.subtotal;
      let subtval = tableStrategy && isSubtotalValueUndefined ? calctotal : v.total;

      if (props && props.prop.priceSeperator === '.' && subtval)
        subtval = Number(subtval?.toString().split(',').join('.'));

      const fdisvalraw =
        v?.discount &&
        helpers.parseFloatCurr({
          prop: props?.prop || null,
          x: v.discount.val,
        });
      const fdisval =
        fdisvalraw &&
        Math.abs(fdisvalraw) * (v?.discount?.type ? v?.discount?.type : fdisvalraw < 0 ? -1 : 1);

      const ftaxvalraw =
        v?.tax && helpers.parseFloatCurr({ prop: props?.prop || null, x: v?.tax?.val });
      const ftaxval =
        ftaxvalraw &&
        Math.abs(ftaxvalraw) * (v?.tax?.type ? v?.tax?.type : ftaxvalraw < 0 ? -1 : 1);

      const subtotaltext = configText ? v?.subtotalText || configText('subtotal text') : 'Subtotal';
      const subtotalBeforeTaxText = configText
        ? v?.[`subtotalBeforeTaxText${tableStrategy ? v?.name || '' : ''}`] ||
          configText('total before tax')
        : 'Total before tax';

      const calcdis = () => {
        if (v?.discount && subt) {
          if (v?.discount.unit === '%') {
            subtval =
              strategy !== 'value' && isSubtotalValueUndefined
                ? calctotal
                : subtval / (1 - (-1 * fdisval) / 100);
          } else {
            subtval =
              strategy !== 'value' && isSubtotalValueUndefined ? calctotal : subtval - fdisval;
          }
        }
      };

      const calctax = () => {
        if (v?.tax && subt) {
          if (v?.tax?.unit === '%') {
            subtval =
              tableStrategy && isSubtotalValueUndefined
                ? calctotal
                : subtval / (1 - (-1 * ftaxval) / 100);
          } else {
            subtval = tableStrategy && isSubtotalValueUndefined ? calctotal : subtval - ftaxval;
          }
        }
      };

      if (tableStrategy) {
        calcdis();
        calctax();
      } else {
        calctax();
        calcdis();
      }

      if (!tableStrategy && v.subtotalValue) {
        subtval = v?.subtotalValue;
      }

      if (v?.subtotal) {
        subtval = concatCurrency(subtval);

        subtotalhtml = (
          <tr className={`sumrow subtrow${subt ? ' btoprow' : ''}`}>
            <td className="name subt-text">{subtotaltext}</td>
            <td className="price total loger-2">
              <Amount amount={subtval} />
            </td>
          </tr>
        );
      }

      // discount
      let discounthtml = '';
      if (v?.discount) {
        let disval = '';
        if (v.discount.unit === '%') {
          disval = `${v?.discount.type ? Math.abs(fdisval) : fdisvalraw || 0}%`;
          calctotal += (fdisval / 100) * calctotal;
        } else {
          disval = concatCurrency(v?.discount.type ? Math.abs(fdisval) : fdisvalraw || 0, {
            curr: v.discount.unit || curr,
          });
          calctotal += fdisval;
        }

        const discValue = () => {
          if (v?.discount?.unit === '%') {
            const minusedValue = (subTotal * fdisval) / 100;
            // const calcDiscValue = helpers.concatcurr({
            //   prop: props?.prop || null,
            //   x: minusedValue || 0,
            //   curr: curr,
            //   limitDec: decimalPrecision,
            //   roundedTotal: roundTotal,
            //   parsingDecimal: roundTotal ? false : true,
            // });
            // return calcDiscValue;
            return concatCurrency(minusedValue, { minimum: null });
          }
          return disval;
        };

        const disunit = v?.discount?.unit === '%' ? 'percent' : 'value';
        const cls = `sumrow discountrow${subt ? '' : ' btoprow'}`;
        discounthtml = (
          <tr className={cls}>
            <td className="name subt-text">
              {v?.discount?.text} {disunit === 'percent' && `(${disval.replaceAll(' ', '')})`}
            </td>
            <td className={`price 2 total unit-${disunit}`}>
              <Amount amount={discValue()} />
            </td>
          </tr>
        );
      }

      // total before tax
      let totalBeforeTaxHtml = '';
      if (v?.subtotalBeforeTax) {
        // Evaluate the value before tax using 'calctotal' expression
        const absoluteCalctotal = utils.evaluateExpression(calctotal);
        const subtotalBeforeTaxValue = concatCurrency(absoluteCalctotal);

        const cls = `sumrow total-before-tax subtrow${subt ? ' btoprow' : ''}`;
        totalBeforeTaxHtml = (
          <tr className={cls}>
            <td className="name subt-text total-before-tax">{subtotalBeforeTaxText}</td>
            <td className="price total total-before-value">
              <Amount amount={subtotalBeforeTaxValue} />
            </td>
          </tr>
        );
      }

      // tax
      let taxhtml = '';
      if (v?.tax) {
        // const taxval = helpers.concatcurr({
        //   prop: props?.prop || null,
        //   x: v?.tax?.type ? Math.abs(ftaxval) : ftaxvalraw || 0,
        //   curr: v?.tax?.unit || curr,
        //   limitDec: decimalPrecision,
        //   roundedTotal: true,
        // });

        let taxval = '';
        let taxUnit = v?.tax?.unit;
        let taxType = v?.tax?.unit;
        if (taxUnit === '%') {
          taxval = `${taxType ? Math.abs(ftaxval) : ftaxvalraw || 0}%`;
        } else {
          taxval = concatCurrency(taxType ? Math.abs(ftaxval) : ftaxvalraw || 0, {
            curr: taxUnit || curr,
            parsingDecimal: undefined,
          });
        }

        if (taxUnit === '%') {
          calctotal += (ftaxval / 100) * calctotal;
        } else {
          calctotal += ftaxval;
        }
        const taxValue = () => {
          if (taxUnit === '%') {
            const totalBeforeTax = calcDiscPrice(
              subTotal,
              v?.discount?.val,
              v?.discount?.unit,
              props?.prop.priceSeperator === '.'
            );
            const addedValue = (totalBeforeTax * ftaxval) / 100;
            // const calcTaxValue = helpers.concatcurr({
            //   prop: props?.prop || null,
            //   x: addedValue || 0,
            //   curr: curr,
            //   limitDec: decimalPrecision,
            //   roundedTotal: roundTotal,
            //   parsingDecimal: roundTotal ? false : true,
            // });
            const calcTaxValue = concatCurrency(addedValue, { parsingDecimal: false });
            return calcTaxValue;
          }
          return taxval;
        };

        const taxunit = taxUnit === '%' ? 'percent' : 'value';
        const cls = `sumrow discountrow taxrow${subt ? '' : ' btoprow'}`;
        taxhtml = (
          <tr className={cls}>
            <td className="name subt-text 2">
              {v?.tax?.text} {taxunit === 'percent' && `(${taxval.replaceAll(' ', '')})`}
            </td>
            <td className={`price total loger-3 unit-${taxunit}`}>
              <Amount amount={taxValue()} />
            </td>
          </tr>
        );
      }

      let total = '';
      if (strategy.indexOf('table') > -1) {
        // total = helpers.concatcurr({
        //   prop: props?.prop || null,
        //   x:
        //     props.prop.priceSeperator === '.'
        //       ? calctotal?.toString().replace('.', ',') || 0
        //       : calctotal || 0,
        //   curr,
        //   minimum: 0,
        //   limitDec: roundTotal ? 0 : decimalPrecision,
        //   roundedTotal: roundTotal,
        //   parsingDecimal: roundTotal ? false : true,
        // });

        total = concatCurrency(calctotal, { limitDec: roundTotal ? 0 : decimalPrecision });
      } else {
        // total = helpers.concatcurr({
        //   prop: props?.prop || null,
        //   x:
        //     props.prop.priceSeperator === '.'
        //       ? v?.total?.toString().replace('.', ',') || 0
        //       : v?.total || 0,
        //   curr,
        //   minimum: 0,
        //   limitDec: roundTotal ? 0 : decimalPrecision,
        //   roundedTotal: roundTotal,
        //   parsingDecimal: roundTotal ? false : true,
        // });

        total = concatCurrency(v?.total, { limitDec: roundTotal ? 0 : decimalPrecision });
      }

      const totaltext = configText ? v?.totalText || configText('total text') : 'Total';

      let showTotal = true;
      if (typeof v?.showTotal === 'undefined') {
        showTotal = true;
      } else {
        showTotal = !!v?.showTotal;
      }

      if (showTotal)
        totalhtml = (
          <tr className="sumrow totalrow">
            <td className="name total">{totaltext}</td>
            <td className="price total price-amount">
              <Amount amount={total} />
            </td>
          </tr>
        );

      let customInput = '';

      if (props?.prop?.selectedOption && strategy === 'value')
        customInput = (
          <label
            className="nobreak-secondary-checkbox"
            style={props.prop.selectedOption ? { display: 'block' } : { display: 'none' }}>
            <input
              type="checkbox"
              checked={
                pkgname.toLowerCase() === props.prop.selectedOption.toLowerCase() ? 'checked' : ''
              }
              disabled
            />
            <span className="nobreak-secondary-checkbox-checkmark h1" />
          </label>
        );

      const hasAnyCheckbox = v?.list?.some((item) => getEffectiveSelectable(item, selectable));
      let fontSizeValue = 0;

      if (getBody) [fontSizeValue] = getBody.match(/\d+/);

      if (fontSizeValue && fontSizeValue <= 14) {
        bigbody.push(
          <div
            className={`nobreak-secondary ${
              hasAnyCheckbox && strategy !== 'value' ? 'checkbox-enabled' : ''
            }`}
            key={`${deliverablesIndex}-body`}>
            {customInput}
            <div className="texblockbody">
              {opheadline}
              <div className="table-wrapper">
                <table
                  className={
                    taxhtml || totalBeforeTaxHtml || discounthtml || subtotalhtml
                      ? 'has-totals'
                      : ''
                  }>
                  <thead className="pricing-table-heading">{tableHeading}</thead>
                  <tbody className="texblockbodyinner">{body ? body : null}</tbody>
                </table>
              </div>
              <table className="texblocktotals">
                <tbody>
                  {(!tableStrategy && subtotalhtml) ||
                  (tableStrategy && subtotalhtml && (discounthtml || taxhtml))
                    ? subtotalhtml
                    : null}
                  {discounthtml ? discounthtml : null}
                  {totalBeforeTaxHtml ? totalBeforeTaxHtml : null}
                  {taxhtml ? taxhtml : null}
                  {totalhtml ? totalhtml : null}
                </tbody>
              </table>
            </div>
          </div>
        );
      } else {
        bigbody.push(
          <div
            className={`nobreak-secondary ${
              hasAnyCheckbox && strategy !== 'value' ? 'checkbox-enabled' : ''
            }`}
            key={`${deliverablesIndex}-body`}>
            {customInput}
            <div className="texblockbody">
              {opheadline}
              <div className="table-wrapper">
                <table
                  className={
                    taxhtml || totalBeforeTaxHtml || discounthtml || subtotalhtml
                      ? 'has-totals'
                      : ''
                  }>
                  <thead className="pricing-table-heading">{tableHeading}</thead>
                  <tbody className="texblockbodyinner">{body ? body : null}</tbody>
                </table>
              </div>
              <table className="texblocktotals">
                <tbody>
                  {(!tableStrategy && subtotalhtml) ||
                  (tableStrategy && subtotalhtml && (discounthtml || taxhtml))
                    ? subtotalhtml
                    : null}
                  {discounthtml ? discounthtml : null}
                  {totalBeforeTaxHtml ? totalBeforeTaxHtml : null}
                  {taxhtml ? taxhtml : null}
                  {totalhtml ? totalhtml : null}
                </tbody>
              </table>
            </div>
          </div>
        );
      }
    });

    return bigbody;
  }
  // -------- END of deliverables --------

  return null;
};

TeXHTMLString.propTypes = {
  content: PropTypes.string.isRequired,
  configText: PropTypes.func,
  props: PropTypes.shape({
    prop: PropTypes.shape({
      currency: PropTypes.string,
      state: PropTypes.string,
      priceSeperator: PropTypes.string,
      decimalPrecision: PropTypes.number,
      language: PropTypes.string,
      selectedOption: PropTypes.string,
      draft: PropTypes.shape({
        bodyFont: PropTypes.shape({
          fontSize: PropTypes.string,
        }),
        section: PropTypes.shape({
          language: PropTypes.string,
        }),
        item: PropTypes.shape({
          language: PropTypes.string,
        }),
      }),
    }),
    name: PropTypes.string,
  }),
  blockIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TeXHTMLString.defaultProps = {
  configText: null,
  props: {
    prop: {
      currency: '$',
      decimalPrecision: 2,
      priceSeperator: ',',
    },
  },
  blockIndex: 0,
};

export default TeXHTMLString;

function Amount({ amount }) {
  return <div className="no-break no-rtl">{amount}</div>;
}

Amount.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const getColumnSize = (timeLength, isLargeName) => {
  if (timeLength > 15) {
    if (!isLargeName) {
      if (timeLength > 21) {
        return 'size-7';
      }
      if (timeLength > 18) {
        return 'size-6';
      }
    }
    return 'size-5';
  }

  if (timeLength > 12) {
    if (!isLargeName && timeLength > 15) {
      return 'size-5';
    }
    return 'size-4';
  }

  if (timeLength > 9) {
    return 'size-3';
  }
  if (timeLength > 5) {
    return 'size-2';
  }
  return 'size-1';
};
